<template>
  <template v-if="!isLoading">
    <Button @click="onGoBack" variant="outline" class="mb-6" aria-label="Go back">
      <ArrowLeft class="h-4 w-4" />
    </Button>
    <div class="rounded-2xl shadow-md border-l border-r border-t border-grey-light-200 mt-0 bg-white">
      <div class="p-6 flex flex-col md:flex-row gap-4">
        <div>
          <h2 class="text-xl text-black font-semibold">Invoice Report</h2>
          <p class="text-grey-light-600">Reference: {{ invoice?.reference }}</p>
          <p v-if="errorMsg !== ''" class="text-sm font-medium text-destructive">{{ errorMsg }}</p>
        </div>
        <div class="flex gap-2 md:ml-auto">
          <Button @click="downloadDocument" variant="outline" :disabled="downloading" aria-label="Download invoice PDF">
            <Download v-if="!downloading" class="h-4 w-4" />
            <LayupSpinner v-else :size="4" />
          </Button>
        </div>
      </div>
      <div class="border-t border-grey-light-200">
        <div
          class="grid grid-cols-1 gap-4 p-8"
          :class="{ 'md:grid-cols-4': invoice?.status, 'md:grid-cols-3': !invoice?.status }"
        >
          <div class="col-span-1">
            <p class="text-grey-light-600 text-xs font-medium">Created Date</p>
            <p class="text-brand-700 text-xl font-semibold">{{ formatDateShort(invoice?.createdAt) }}</p>
          </div>
          <div class="col-span-1" v-if="invoice?.status">
            <p class="text-grey-light-600 text-xs font-medium">Status</p>
            <Badge :variant="getInvoiceStatusVariant(invoice?.status)">{{ invoice?.status }}</Badge>
          </div>
          <div class="col-span-1">
            <p class="text-grey-light-600 text-xs font-medium">Amount excl VAT</p>
            <p class="text-brand-700 text-xl font-semibold">{{ displayCurrency(invoice?.exVatAmount || 0) }}</p>
          </div>
          <div class="col-span-1">
            <p class="text-grey-light-600 text-xs font-medium">Total Amount</p>
            <p class="text-brand-700 text-xl font-extrabold">{{ displayCurrency(invoice?.totalAmount || 0) }}</p>
          </div>
        </div>
      </div>
    </div>
    <div class="mt-6">
      <div class="rounded-2xl border border-gray-200 overflow-hidden bg-white">
        <div class="px-6 py-8 flex justify-between">
          <DropdownMenu>
            <DropdownMenuTrigger>
              <div class="flex items-center gap-2">
                <h2 class="text-lg font-semibold text-gray-900 cursor-pointer">{{ feesTabMap[feesTabKey] }}</h2>
                <ChevronDown class="w-4 h-4 text-gray-700" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" :sideOffset="10" class="w-[120px] p-0">
              <DropdownMenuItem
                v-for="[key, title] in Object.entries(feesTabMap)"
                :key="key"
                @click="updateFeesTabKey(key)"
                class="cursor-pointer"
              >
                <h2 class="text-sm text-gray-900">{{ title }}</h2>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            v-if="feesTabKey === 'detailed'"
            @click="downloadFeesSpreadsheet"
            variant="outline"
            :disabled="isDownloadingFeesSpreadsheet"
            aria-label="Download fees spreadsheet"
            size="sm"
          >
            <Download v-if="!isDownloadingFeesSpreadsheet" class="h-2 w-2" />
            <LayupSpinner v-else :size="2" />
          </Button>
        </div>
        <FeeSummary v-if="feesTabKey === 'summary'" :invoice="invoice" />
        <FeeDetails v-if="feesTabKey === 'detailed'" :id="id" />
      </div>
    </div>
  </template>
  <div class="flex justify-center">
    <LayupSpinner :size="10" v-if="isLoading" />
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { NetworkError } from '@/errors';
import { downloadInvoicePdf, downloadInvoiceXlsx, getInvoiceById } from '@/services/invoices';
import { displayCurrency, formatDateShort } from '@/utils/format.utils';
import { getInvoiceStatusVariant } from '@/utils/invoice.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { ArrowLeft, ChevronDown, Download } from 'lucide-vue-next';
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { ModelInvoiceReport } from '@layup/api-sdk';
import FeeDetails from './FeeDetails.vue';
import FeeSummary from './FeeSummary.vue';

type FeesTabKey = 'summary' | 'detailed';

const router = useRouter();
const route = useRoute();
const id = route.params.id as string;

const isLoading = ref(true);
const downloading = ref(false);
const errorMsg = ref('');
const invoice = ref<ModelInvoiceReport>();

const feesTabKey = ref<FeesTabKey>('summary');
const feesTabMap: Record<FeesTabKey, string> = {
  summary: 'Fee Summary',
  detailed: 'Fee Details',
};

const isDownloadingFeesSpreadsheet = ref(false);

const downloadFeesSpreadsheet = async () => {
  isDownloadingFeesSpreadsheet.value = true;

  try {
    const responseData = await downloadInvoiceXlsx(id);

    const link = document.createElement('a');
    link.href = responseData.redirectUrl!;
    link.target = '_blank';
    link.setAttribute('download', `${id}-${invoice.value?.reference}-invoice-report.pdf`);
    link.click();
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('invoice-details', { status: 'network_error', channel: 'merchant', method: 'downloadFeesSpreadsheet' });
    } else {
      createException(error);
      addMetric('invoice-details', { status: 'error', channel: 'merchant', method: 'downloadFeesSpreadsheet' });
    }
  } finally {
    isDownloadingFeesSpreadsheet.value = false;
  }
};

const updateFeesTabKey = (key: string) => (feesTabKey.value = key as FeesTabKey);

onMounted(async () => await loadInvoice());

const loadInvoice = async () => {
  try {
    isLoading.value = true;

    if (!id) {
      router.push({ name: 'merchantInvoices' });
    }

    const invoiceData = await getInvoiceById(id);
    invoice.value = invoiceData;
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('invoice-details', { status: 'network_error', channel: 'merchant', method: 'loadInvoice' });
    } else {
      createException(error);
      addMetric('invoice-details', { status: 'error', channel: 'merchant', method: 'loadInvoice' });
    }
  } finally {
    isLoading.value = false;
  }
};

const downloadDocument = async () => {
  try {
    if (!id) {
      errorMsg.value = 'No invoice ID found. Please contact support.';
      return;
    }

    const url = await downloadInvoicePdf(id);

    if (!url.redirectUrl) {
      errorMsg.value = 'No download URL found. Please contact support.';
      return;
    }

    const link = document.createElement('a');
    link.href = url.redirectUrl;
    link.target = '_blank';
    link.setAttribute('download', `${id}-${invoice.value?.reference}-invoice-report.pdf`);
    link.click();

    addMetric('invoice-details-pdf-download', { status: 'success', channel: 'merchant' });
  } catch (error: any) {
    errorMsg.value = error.response?.data ?? 'Something went wrong';

    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('invoice-details-pdf-download', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('invoice-details-pdf-download', { status: 'error', channel: 'merchant' });
    }
  }
};

const onGoBack = () => {
  router.push({ name: 'merchantInvoices' });
};
</script>
